import httpAxios from "@/axios";
import { router } from "@/main";

import { Popup, ErrorPopup } from "../../core/classes/BaseStore";
import $i18n from "@/i18n";

export class ApiAsync {
  constructor(basePath, model, hooks, custom, cacheResult) {
    this.Basepath = basePath;
    this.Model = model;
    this.Hooks = hooks ? hooks : {};
    this.Custom = custom ? custom : {};
    this.Cache = cacheResult ? cacheResult : false;

    this.$http = httpAxios;

    this.LocalStorageAvailable = false;

    if (window.localStorage) {
      this.LocalStorageAvailable = true;

      // Check if api storage is available
      if (!window.localStorage["vuex-api-" + window.btoa(this.Basepath)]) {
        window.localStorage["vuex-api-" + window.btoa(this.Basepath)] = "{}";
      }
    }
  }

  /**
   * Trigger api hooks
   * @param {*} name
   */
  TriggerHooks(name, data, arg3) {
    if (!this.Hooks || !this.Hooks[name]) {
      return;
    }

    this.Hooks[name](name, data, arg3);
  }

  GetLocalStorage() {
    let StorageData = JSON.parse(
      window.localStorage["vuex-api-" + window.btoa(this.Basepath)]
    );
    return StorageData;
  }

  UpdateLocalStorage(data) {
    let StorageData = this.GetLocalStorage();
    StorageData = Object.assign(StorageData, data);
    window.localStorage[
      "vuex-api-" + window.btoa(this.Basepath)
    ] = JSON.stringify(StorageData);
  }

  UpdateSingleLocalStorage(id, data) {
    let Data = {};
    Data[id] = data;
    this.UpdateLocalStorage(Data);
  }

  async asyncCall(Method, ...Call) {
    let errors;
    const ApiResult = await this.$http[Method](...Call).then(null, (err) => {
      if (err.response.data) {
        if (err.response.data.status === "401") {
          router.push("/logout");
        }
        errors = err.response.data.data
          ? err.response.data.data.errors
          : err.response.data.message
          ? [err.response.data.message]
          : null;
      }

      ErrorPopup(err, errors);
      throw Error(err);
    });

    return ApiResult.data.data;
  }

  /**
   * Returns all entries
   * @param {*} params
   */
  async GetAll(params, UseCache) {
    let StorageData = null;
    let Result = null;
    let data = null;

    if (process.env.NODE_ENV === "development") {
      UseCache = false;
    }

    this.TriggerHooks("PreGetAll", params);

    if (this.Cache && UseCache === true) {
      StorageData = this.GetLocalStorage();
      Result = Object.values(StorageData);
    }

    if (this.Cache === false || Result === null) {
      const ApiResult = await this.asyncCall(
        "get",
        this.Basepath + "/all",
        params
      );

      Result = ApiResult;

      // Save the result
      if (this.Cache === true && UseCache !== true) {
        StorageData = this.GetLocalStorage();
        ApiResult.forEach((Item) => {
          StorageData[Item.id] = Item;
        });
        this.UpdateLocalStorage(StorageData);
      }
    }

    if (typeof this.Model === 'function') {
      let model = this.Model;
      data = Result.map((Entry) => {
        return new model(Entry);
      });
    } else {
      data = Result;
    }

    this.TriggerHooks("AfterGetAll", data);

    return data;
  }

  /**
   * Get a list of params
   * @param {*} params
   */
  async Get(params) {
    let data = null;

    this.TriggerHooks("PreGet", params);

    const ApiResult = await this.asyncCall("get", this.Basepath, { params });

    if (typeof this.Model === 'function' && ApiResult.data) {
      let model = this.Model;
      ApiResult.data = ApiResult.data.map((Entry) => {
        return new model(Entry);
      });
    }

    data = ApiResult;

    this.TriggerHooks("AfterGet", data);

    return data;
  }

  /**
   * Create a new entry
   * @param {*} params
   */
  async Create(params) {
    let data = null;

    try {
      this.TriggerHooks("PreCreate", params);

      const ApiResult = await this.asyncCall("post", this.Basepath, params);

      if (typeof this.Model === 'function') {
        let model = this.Model;
        data = new model(ApiResult);
      } else {
        data = ApiResult;
      }

      this.TriggerHooks("AfterCreate", data);
    } catch (error) {
      this.TriggerHooks("ErrorCreate", data);
      return null;
    }

    return data;
  }

  /**
   * Show a specific entry
   * @param {*} Id
   * @param {*} Params
   */
  async Show(Id, Params, UseCache) {
    let Result = null;

    if (process.env.NODE_ENV === "development") {
      UseCache = false;
    }

    Params = Params ? Params : {};

    if (this.Cache && UseCache === true) {
      const StorageData = this.GetLocalStorage();
      Result = StorageData[Id] ? StorageData[Id] : null;
    }

    if (this.Cache === false || Result === null) {
      const ApiResult = await this.asyncCall(
        "get",
        this.Basepath + "/" + Id,
        Params
      );

      Result = ApiResult;

      if (this.Cache) {
        this.UpdateSingleLocalStorage(Id, Result);
      }
    }

    let data = null;

    this.TriggerHooks("PreShow", Id, Params);

    if (typeof this.Model === 'function') {
      let model = this.Model;
      data = new model(Result);
    } else {
      data = Result;
    }

    this.TriggerHooks("AfterShow", data);

    return data;
  }

  /**
   * Update a specfic entry
   * @param {*} Id
   * @param {*} Data
   * @param {*} Params
   */
  async Update(Id, Data, Params) {
    const ApiResult = await this.asyncCall(
      "put",
      this.Basepath + "/" + Id,
      Data,
      {
        params: Params,
      }
    );

    let data = null;

    this.TriggerHooks("PreUpdate", Id, Data, Params);

    if (typeof this.Model === 'function') {
      let model = this.Model;
      data = new model(ApiResult);
    } else {
      data = ApiResult;
    }

    this.TriggerHooks("AfterUpdate", data);

    return data;
  }

  /**
   * Delete a specific entry
   * @param {*} Id
   * @param {*} Params
   */
  async Destroy(Id, Params) {
    try {
      this.TriggerHooks("PreDestroy", Id, Params);

      const ApiResult = await this.asyncCall(
        "delete",
        this.Basepath + "/" + Id,
        Params ? Params : {}
      );

      this.TriggerHooks("AfterDestroy", Id, Params);

      return true;
    } catch (err) {
      this.TriggerHooks("ErrorDestroy", Id, Params);

      return false;
    }
  }

  /**
   * Stats
   * @param {*} Id
   * @param {*} Params
   */
  async Stats(Params) {
    let Result = null;

    Params = Params ? Params : {};

    const ApiResult = await this.asyncCall("get", this.Basepath + "/stats", {
      params: Params,
    });

    Result = ApiResult;

    let data = null;

    this.TriggerHooks("PreStats", Params);

    this.TriggerHooks("AfterStats", ApiResult);

    return ApiResult;
  }

  /**
   * Call custom getter
   * @param {*} params
   */
  async CallCustom(name, settings) {
    const classAccess = this;
    let result = null;

    this.TriggerHooks("PreCustom", name);

    if (this.Custom[name]) {
      result = await this.Custom[name](classAccess, settings);
    }

    this.TriggerHooks("AfterCustom", name);

    return result;
  }

  /**
   * Convert a data to model
   * @param {*} data
   */
  async ConvertDataToModel(data) {
    if (typeof this.Model === 'function') {
      let model = this.Model;
      data = new model(data);
    }
    return data;
  }
}

export class ApiAsyncHelper {
  constructor() {
    this.$http = httpAxios;
  }

  async call(Method, ...Call) {
    let errors;
    const ApiResult = await this.$http[Method](...Call).then(null, (err) => {
      if (err.response.data) {
        if (err.response.data.status === "401") {
          router.push("/logout");
        }
        errors = err.response.data.data
          ? err.response.data.data.errors
          : err.response.data.message
          ? [err.response.data.message]
          : null;
      }

      ErrorPopup(err, errors);
      throw Error(err);
    });

    return ApiResult.data.data;
  }
}
